import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  HelpCircle, 
  BookOpen, 
  Calculator, 
  TrendingUp, 
  Search,
  BarChart3,
} from 'lucide-react';
import SeoHead from '@/components/shared/SeoHead';
import { generateFAQPageSchema, generateOrganizationSchema, generateMultipleSchemas } from '@/lib/schema';
import { LeaderboardAd, InContentAd } from '@/components/shared/AdSense';
import AdFrame from '@/components/shared/AdFrame';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

const Support = () => {
  const baseUrl = 'https://wageatlas.com';
  const pageUrl = `${baseUrl}/support/`;

  const faqs = [
    {
      question: "What data sources does Wage Atlas use?",
      answer: "Wage Atlas uses official U.S. Bureau of Labor Statistics (BLS) Occupational Employment and Wage Statistics (OEWS) data, updated annually. We also incorporate Bureau of Economic Analysis (BEA) Regional Price Parities for cost-of-living adjustments. All data comes directly from government sources - we don't use estimates or crowdsourced data."
    },
    {
      question: "How often is the salary data updated?",
      answer: "Our data is updated annually when the BLS releases new OEWS survey results, typically in the spring. We also update our database throughout the year as additional data becomes available. Historical data goes back to 1988, allowing for long-term trend analysis."
    },
    {
      question: "What's the difference between mean and median salary?",
      answer: "Mean salary is the average of all reported salaries, which can be skewed by very high or very low earners. Median salary is the middle value when all salaries are ordered - half earn more, half earn less. Median is often more representative of a 'typical' worker's salary. We show both metrics to give you a complete picture."
    },
    {
      question: "How do I use the salary calculator?",
      answer: "Our salary calculator converts between hourly, daily, weekly, monthly, and annual rates. Simply enter any salary amount and select the time period (e.g., $25 per hour), and the calculator will show you equivalent amounts in all other time periods. This is useful for comparing job offers or understanding your total compensation."
    },
    {
      question: "What are Regional Price Parities (RPP) and real wages?",
      answer: "Regional Price Parities adjust for cost-of-living differences across locations. A 'real wage' accounts for purchasing power - $80,000 in San Francisco doesn't go as far as $80,000 in a lower-cost area. You can freely toggle between nominal (actual dollar amount) and real (cost-of-living adjusted) wages for accurate comparisons - it's free for everyone."
    },
    {
      question: "Can I export salary data?",
      answer: "Yes. Salary data and CSV exports are free for everyone. We also offer a free PDF report with a detailed salary breakdown, historical trends, growth outlook and AI-risk analysis - perfect for salary negotiations or research documentation. Just enter your email on any salary page and we'll deliver it to your inbox."
    },
    {
      question: "How do I find my occupation's SOC code?",
      answer: "You can search for occupations by name on our platform - we'll show you the corresponding SOC code. We also have a comprehensive SOC Code Guide that explains the classification system and helps you find your occupation's code. SOC codes are used by the BLS to organize occupations consistently."
    },
    {
      question: "Is Wage Atlas really free?",
      answer: "Yes. All salary data, full percentiles (P10-P90), cost-of-living adjusted real wages, historical trends, maps, comparisons, career tools and PDF reports are 100% free with no view limits. Wage Atlas is supported by advertising and partner referrals. There are no paid products."
    },
    {
      question: "How accurate is the salary data?",
      answer: "Our data comes directly from the U.S. Bureau of Labor Statistics, which surveys over 1.2 million businesses annually. The data represents actual wages paid to workers, not estimates. However, individual salaries can vary based on experience, education, company size, and other factors. Our data shows market ranges and percentiles to account for this variation."
    },
    {
      question: "Can I compare salaries across multiple locations?",
      answer: "Yes! Our platform allows you to compare salaries for any occupation across different states and metropolitan areas, side-by-side with real wage adjustments - free for everyone. This is perfect for job seekers considering relocation or professionals negotiating remote work arrangements."
    },
    {
      question: "What if I can't find my occupation?",
      answer: "We cover 800+ occupations from the BLS OEWS survey. If you can't find your specific role, try searching for related occupations or broader categories. You can also contact our support team - we may be able to help you find the closest match or explain how your role might be classified."
    },
    {
      question: "How do I interpret percentile data?",
      answer: "Percentiles show salary distribution. The 25th percentile means 25% of workers earn less, 75% earn more. The 50th percentile is the median. The 90th percentile represents top earners. Use percentiles to understand where you fall in the market and set realistic salary expectations based on your experience level."
    }
  ];

  // Generate structured data
  const faqPageSchema = generateFAQPageSchema(faqs);
  const organizationSchema = generateOrganizationSchema(baseUrl);
  const structuredData = generateMultipleSchemas([faqPageSchema, organizationSchema]);

  const handleFAQClick = (question: string) => {
    if (typeof window !== 'undefined' && (window as unknown).gtag) {
      (window as unknown).gtag('event', 'faq_click', {
        event_category: 'support',
        question: question,
      });
    }
  };

  const handleFAQExpand = (question: string, isExpanded: boolean) => {
    if (isExpanded && typeof window !== 'undefined' && (window as unknown).gtag) {
      (window as unknown).gtag('event', 'faq_expand', {
        event_category: 'support',
        question: question,
      });
    }
  };

  return (
    <>
      <SeoHead
        title="Support & Help Center - Wage Atlas"
        description="Get help with Wage Atlas. Find answers to frequently asked questions, learn how to use our tools, and access support resources."
        canonical={pageUrl}
        schemas={structuredData}
      />

      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-6xl mx-auto">
            <div className="space-y-8">
              {/* Header */}
              <div className="text-center space-y-4">
                <div className="flex items-center justify-center gap-2 mb-4">
                  <HelpCircle className="h-8 w-8 text-primary" />
                  <h1 className="text-3xl font-bold">Support & Help Center</h1>
                </div>
                <p className="text-lg text-muted-foreground">
                  Find answers to common questions and learn how to get the most out of Wage Atlas.
                </p>
              </div>

              {/* Leaderboard Ad */}
              <AdFrame className="mb-8">
                <LeaderboardAd />
              </AdFrame>

              <div className="grid lg:grid-cols-4 gap-8">
                <div className="lg:col-span-3 space-y-8">
                  {/* Quick Links */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2">
                          <BookOpen className="h-4 w-4 text-primary" />
                          Getting Started
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground mb-3">
                          New to Wage Atlas? Learn the basics.
                        </p>
                        <Button asChild variant="outline" size="sm" className="w-full">
                          <Link to="#getting-started">View Guide</Link>
                        </Button>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2">
                          <Calculator className="h-4 w-4 text-primary" />
                          Tools & Features
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground mb-3">
                          Learn how to use our calculators and tools.
                        </p>
                        <Button asChild variant="outline" size="sm" className="w-full">
                          <Link to="#features">View Tutorials</Link>
                        </Button>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2">
                          <HelpCircle className="h-4 w-4 text-primary" />
                          Contact Support
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground mb-3">
                          Can't find an answer? Reach our support team.
                        </p>
                        <Button asChild variant="outline" size="sm" className="w-full">
                          <Link to="/contact">Get in Touch</Link>
                        </Button>
                      </CardContent>
                    </Card>
                  </div>

                  {/* FAQ Section */}
                  <Card id="faq">
                    <CardHeader>
                      <CardTitle>Frequently Asked Questions</CardTitle>
                      <CardDescription>
                        Find answers to the most common questions about Wage Atlas.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Accordion type="single" collapsible className="w-full">
                        {faqs.map((faq, index) => (
                          <AccordionItem 
                            key={index} 
                            value={`item-${index}`}
                            onClick={() => handleFAQClick(faq.question)}
                          >
                            <AccordionTrigger 
                              onClick={() => handleFAQExpand(faq.question, true)}
                            >
                              {faq.question}
                            </AccordionTrigger>
                            <AccordionContent>
                              <p className="text-muted-foreground">{faq.answer}</p>
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    </CardContent>
                  </Card>

                  {/* Getting Started Guide */}
                  <Card id="getting-started">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <BookOpen className="h-5 w-5 text-primary" />
                        Getting Started Guide
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <h3 className="font-semibold mb-2">1. Search for Salary Data</h3>
                        <p className="text-sm text-muted-foreground mb-3">
                          Use our search to find salary information for any occupation in any location.
                        </p>
                        <Button asChild variant="outline" size="sm">
                          <Link to="/search">
                            <Search className="mr-2 h-4 w-4" />
                            Try Search
                          </Link>
                        </Button>
                      </div>
                      <div>
                        <h3 className="font-semibold mb-2">2. Explore Salary Trends</h3>
                        <p className="text-sm text-muted-foreground mb-3">
                          View historical wage data and trends for occupations over time.
                        </p>
                        <Button asChild variant="outline" size="sm">
                          <Link to="/trends">
                            <TrendingUp className="mr-2 h-4 w-4" />
                            View Trends
                          </Link>
                        </Button>
                      </div>
                      <div>
                        <h3 className="font-semibold mb-2">3. Use Our Calculators</h3>
                        <p className="text-sm text-muted-foreground mb-3">
                          Convert between hourly, annual, and other salary formats.
                        </p>
                        <Button asChild variant="outline" size="sm">
                          <Link to="/calculators">
                            <Calculator className="mr-2 h-4 w-4" />
                            Use Calculators
                          </Link>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Feature Tutorials */}
                  <Card id="features">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <BarChart3 className="h-5 w-5 text-primary" />
                        Feature Tutorials
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div>
                        <h3 className="font-semibold mb-2">Salary Calculator</h3>
                        <p className="text-sm text-muted-foreground mb-2">
                          Convert between different salary formats (hourly, annual, monthly, etc.). 
                          Perfect for comparing job offers or understanding your total compensation.
                        </p>
                        <Link to="/calculators/salary" className="text-sm text-primary hover:underline">
                          Learn more →
                        </Link>
                      </div>
                      <div>
                        <h3 className="font-semibold mb-2">Trend Analysis</h3>
                        <p className="text-sm text-muted-foreground mb-2">
                          View historical wage trends from 1988 to present. See how salaries have 
                          changed over time for any occupation and location.
                        </p>
                        <Link to="/trends" className="text-sm text-primary hover:underline">
                          Learn more →
                        </Link>
                      </div>
                      <div>
                        <h3 className="font-semibold mb-2">Data Exports</h3>
                        <p className="text-sm text-muted-foreground mb-2">
                          Export salary data as CSV, or request a free PDF report with detailed
                          breakdowns - delivered straight to your inbox.
                        </p>
                        <Link to="/ai-career-analysis" className="text-sm text-primary hover:underline">
                          Learn more →
                        </Link>
                      </div>
                    </CardContent>
                  </Card>

                  {/* In-Content Ad */}
                  <InContentAd />

                  {/* Still Need Help */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Still Need Help?</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground mb-4">
                        Can't find what you're looking for? Our support team is here to help.
                      </p>
                      <Button asChild>
                        <Link to="/contact">
                          <HelpCircle className="mr-2 h-4 w-4" />
                          Contact Support
                        </Link>
                      </Button>
                    </CardContent>
                  </Card>
                </div>

                {/* Sidebar */}
                <div className="hidden lg:block space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Quick Links</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <Link 
                        to="#faq" 
                        className="block text-sm text-muted-foreground hover:text-primary transition-colors"
                      >
                        FAQ
                      </Link>
                      <Link 
                        to="#getting-started" 
                        className="block text-sm text-muted-foreground hover:text-primary transition-colors"
                      >
                        Getting Started
                      </Link>
                      <Link 
                        to="#features" 
                        className="block text-sm text-muted-foreground hover:text-primary transition-colors"
                      >
                        Features
                      </Link>
                      <Link 
                        to="/contact" 
                        className="block text-sm text-muted-foreground hover:text-primary transition-colors"
                      >
                        Contact Support
                      </Link>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Support;
